import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// project imports
import Loader from "components/Loader";
import {
  canManageCrowdPrediction,
  isCrowdPredictionPath,
} from "utils/crowdPredictionAccess";
import useAuth from "hooks/useAuth";

// types
import { GuardProps } from "types/auth";

// ==============================|| AUTH GUARD ||============================== //

const WEB_APP_ALLOWED_POSITIONS = new Set([
  "super_admin",
  "admin",
  "manager",
  "lead_supervisor",
]);
const ADMIN_ONLY_PATH_PREFIXES = [
  "/pages/",
  "/pearson-map",
  "/facility-map",
  "/facility-modelling",
  "/airport-map",
  "/washroom-map",
  "/task-settings",
  "/task-settings-2",
  "/shift-scheduling",
  "/shift-scheduler",
  "/staffing-calendar",
  "/master-schedule",
  "/task-lines",
  "/task-assignment",
  "/task-scheduling",
  "/staff-management",
  "/edit-timeline",
  "/deterioration",
  "/administration/user-management/admin-setting",
  "/administration/user-management/create-user",
];
const SUPER_ADMIN_ONLY_PATH_PREFIXES = [
  "/administration/hr-integrations",
  "/administration/airport-graph-builder",
];
const GENERAL_ADMIN_PATH_PREFIXES = [
  "/administration/airport-setup",
  "/administration/airport-graph-builder",
];

export default function AuthGuard({ children }: GuardProps) {
  const { isInitialized, isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isInitialized && !isLoggedIn) {
      navigate("/login", {
        state: { from: location.pathname },
        replace: true,
      });
      return;
    }

    if (!isInitialized || !isLoggedIn) return;

    const positionKey = String(user?.positionKey || "").toLowerCase();
    const hasWebAccess = WEB_APP_ALLOWED_POSITIONS.has(positionKey);
    if (!hasWebAccess) {
      if (typeof logout === "function") logout();
      navigate("/login", {
        state: {
          from: location.pathname,
          authError:
            "Web app access is restricted to admin, lead_supervisor, and manager roles.",
        },
        replace: true,
      });
      return;
    }

    const isAdmin = positionKey === "admin" || positionKey === "super_admin";
    const isSuperAdmin = positionKey === "super_admin";
    const currentPath = location.pathname || "/";
    if (
      currentPath.startsWith("/administration/airport-travel-times") &&
      !isAdmin &&
      positionKey !== "manager"
    ) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (
      isSuperAdmin &&
      !user?.airportId &&
      !GENERAL_ADMIN_PATH_PREFIXES.some(
        (prefix) => currentPath === prefix || currentPath.startsWith(prefix),
      )
    ) {
      navigate("/administration/airport-setup", { replace: true });
      return;
    }
    if (
      !isSuperAdmin &&
      SUPER_ADMIN_ONLY_PATH_PREFIXES.some(
        (prefix) => currentPath === prefix || currentPath.startsWith(prefix),
      )
    ) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (
      isCrowdPredictionPath(currentPath) &&
      !canManageCrowdPrediction(positionKey)
    ) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (!isAdmin) {
      const adminOnly = ADMIN_ONLY_PATH_PREFIXES.some(
        (prefix) => currentPath === prefix || currentPath.startsWith(prefix),
      );
      if (adminOnly) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [
    isInitialized,
    isLoggedIn,
    navigate,
    location.pathname,
    logout,
    user?.positionKey,
    user?.airportId,
  ]);

  if (!isInitialized) {
    return <Loader />;
  }

  // Block mounting the editor (and its requests) while redirecting unauthorized roles.
  if (
    !isLoggedIn ||
    (isCrowdPredictionPath(location.pathname) &&
      !canManageCrowdPrediction(user?.positionKey))
  )
    return <Loader />;
  return children;
}
